import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Star, MessageSquare, Check, X, IndianRupee } from 'lucide-react';
import { SEO } from '../components/common/SEO';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { UPI_ID, PAYEE_NAME, WHATSAPP_NUMBER, FEATURED_AMOUNT, QR_IMAGE_URL } from '../data/constants';

const API = process.env.REACT_APP_BACKEND_URL || '';

export const ContactPage = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [showFeaturedQR, setShowFeaturedQR] = useState(false);
  const [featuredPaymentInitiated, setFeaturedPaymentInitiated] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setIsMobile(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await axios.post(`${API}/api/contact`, form); toast.success('Message sent successfully!'); setForm({ name: '', email: '', phone: '', message: '' }); }
    catch (e) { toast.error('Failed to send message'); }
    setLoading(false);
  };

  const handleFeaturedPayment = () => {
    const upiLink = `upi://pay?pa=${UPI_ID}&pn=${encodeURIComponent(PAYEE_NAME)}&am=${FEATURED_AMOUNT}&cu=INR&tn=${encodeURIComponent('Featured Listing - JustOffs')}`;
    
    if (isMobile) {
      toast.info('Redirecting to UPI app...');
      window.location.href = upiLink;
      setTimeout(() => {
        setFeaturedPaymentInitiated(true);
        toast.success('Complete payment in your UPI app');
      }, 1500);
    } else {
      setShowFeaturedQR(true);
    }
  };

  const handleWhatsAppConfirm = () => {
    const message = encodeURIComponent(`Hi! I have paid ₹${FEATURED_AMOUNT} for JustOffs Featured Listing. Please confirm and activate my featured listing.`);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="contact-page">
      <SEO title="Contact Us - JustOffs | Get in Touch" description="Have questions about JustOffs? Contact us via WhatsApp or send us a message. We're here to help local businesses and customers." />
      <Header />
      <div className="max-w-lg mx-auto px-4 py-10">
        <h1 className="text-2xl font-bold text-slate-800 mb-2 text-center">Contact Us</h1>
        <p className="text-slate-500 text-center mb-6 text-sm">Have questions? We'd love to hear from you.</p>
        
        {/* Get Featured Section */}
        <div className="bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-200 rounded-2xl p-6 mb-8">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl flex items-center justify-center">
              <Star className="w-6 h-6 text-white fill-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800">Get Featured on JustOffs</h2>
              <p className="text-sm text-slate-600">Promote your business for better visibility</p>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-4 mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-2xl font-bold text-orange-600">₹499</span>
              <span className="bg-orange-100 text-orange-700 text-xs px-3 py-1 rounded-full font-medium">Valid for 1 month</span>
            </div>
            <p className="text-sm text-slate-600">Your listing will appear at the top with a "Featured" badge</p>
          </div>
          
          <button onClick={handleFeaturedPayment} className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 rounded-xl font-semibold hover:from-orange-600 hover:to-orange-700 transition flex items-center justify-center gap-2 shadow-md" data-testid="get-featured-btn">
            <IndianRupee className="w-5 h-5" /> Get Featured Now
          </button>
          
          {featuredPaymentInitiated && (
            <div className="mt-4 bg-green-50 border border-green-200 rounded-xl p-4 text-center">
              <p className="text-green-700 text-sm mb-3">✅ Complete payment in your UPI app</p>
              <button onClick={handleWhatsAppConfirm} className="bg-green-500 text-white px-4 py-2 rounded-lg font-medium text-sm hover:bg-green-600 transition flex items-center justify-center gap-2 mx-auto" data-testid="whatsapp-confirm-btn">
                <MessageSquare className="w-4 h-4" /> Confirm Payment on WhatsApp
              </button>
            </div>
          )}
        </div>

        <button onClick={() => window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=Hi%20JustOffs%20Team!`, '_blank')} className="w-full bg-green-500 text-white py-3 rounded-xl font-semibold mb-6 flex items-center justify-center gap-2 hover:bg-green-600 transition" data-testid="whatsapp-chat-btn">
          <MessageSquare className="w-5 h-5" /> Chat on WhatsApp
        </button>
        <div className="text-center text-slate-400 text-sm mb-6">— or send us a message —</div>
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm p-5 space-y-4" data-testid="contact-form">
          <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Name *</label><input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm" placeholder="Your name" required data-testid="name-input" /></div>
          <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Mobile Number *</label><input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm" placeholder="Your mobile number" required data-testid="phone-input" /></div>
          <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Email</label><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm" placeholder="your@email.com (optional)" data-testid="email-input" /></div>
          <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Message *</label><textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm h-28" placeholder="Your message..." required data-testid="message-input" /></div>
          <button type="submit" disabled={loading} className="w-full bg-orange-500 text-white py-3 rounded-xl font-semibold hover:bg-orange-600 transition disabled:opacity-70" data-testid="submit-btn">{loading ? 'Sending...' : 'Send Message'}</button>
        </form>
      </div>

      {/* QR Code Modal */}
      {showFeaturedQR && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowFeaturedQR(false)} data-testid="qr-modal">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 text-center relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowFeaturedQR(false)} className="absolute top-4 right-4 p-1 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
            <div className="w-14 h-14 bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl flex items-center justify-center mx-auto mb-3">
              <Star className="w-7 h-7 text-white fill-white" />
            </div>
            <h3 className="text-xl font-bold text-slate-800 mb-1">Get Featured - ₹499</h3>
            <p className="text-slate-500 text-sm mb-4">Valid for 1 month</p>
            
            <div className="bg-slate-50 rounded-xl p-4 mb-4">
              <img src={QR_IMAGE_URL} alt="UPI QR Code" className="w-56 h-56 mx-auto rounded-lg" />
            </div>
            
            <div className="text-sm text-slate-600 mb-4">
              <p className="font-medium">UPI ID: {UPI_ID}</p>
              <p>Payee: {PAYEE_NAME}</p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
              <p className="text-amber-700 text-sm">📱 Scan with any UPI app (PhonePe, GPay, Paytm)</p>
            </div>
            
            <button onClick={() => { setShowFeaturedQR(false); setFeaturedPaymentInitiated(true); }} className="w-full bg-green-500 text-white py-3 rounded-xl font-semibold hover:bg-green-600 transition flex items-center justify-center gap-2">
              <Check className="w-5 h-5" /> I've Completed Payment
            </button>
            
            <button onClick={handleWhatsAppConfirm} className="w-full mt-3 bg-white text-green-600 border border-green-200 py-3 rounded-xl font-semibold hover:bg-green-50 transition flex items-center justify-center gap-2">
              <MessageSquare className="w-5 h-5" /> Confirm on WhatsApp
            </button>
          </div>
        </div>
      )}
      
      <Footer />
    </div>
  );
};
